import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  type AgentCreate,
  type AgentUpdate,
  attachSkillsToAgentMutation,
  createAgentMutation,
  detachSkillFromAgent,
  getAgentQueryKey,
  listAgentSkills,
  listAgentSkillsQueryKey,
  listAgentsQueryKey,
  updateAgentMutation,
} from "@/api";
import type { AgentFormState } from "@/features/build/agents/AgentForm";
import type { DraftView } from "../draftModel";
import { useDraftActions } from "../useDraftActions";
import { useDraftRun } from "../useDraftRun";

/** The agent draft as propose_draft validated it (AgentCreate, team optional, plus skills to attach). */
export type AgentDraftBody = Partial<AgentCreate> & { skillUuids?: string[] };

/** Create (agent, then its skills) or Save (PATCH, then the skill set) for an agent draft card. */
export function useAgentDraft(draft: DraftView) {
  const { t } = useTranslation("buildChat", { keyPrefix: "agent" });
  const navigate = useNavigate();
  const actions = useDraftActions();
  const create = useMutation(createAgentMutation());
  const update = useMutation(updateAgentMutation());
  const attach = useMutation(attachSkillsToAgentMutation());
  const agentUuid = useRef<string | null>(draft.targetUuid);

  const { skillUuids, teamUuid: _team, ...body } = draft.draft as AgentDraftBody;
  const attachSkills = (skills: string[]) =>
    attach.mutateAsync({
      path: { agent_uuid: agentUuid.current ?? "" },
      body: skills.map((skillUuid) => ({ skillUuid, isEnabled: true })),
      throwOnError: true,
    });
  // Save makes the agent's skills the draft's: platform skills are implicit and stay.
  const syncSkills = async (wanted: string[]) => {
    const uuid = agentUuid.current ?? "";
    const { data } = await listAgentSkills({ path: { agent_uuid: uuid }, throwOnError: true });
    const current = data.data.filter((s) => s.skill.extension?.extensionType !== "platform").map((s) => s.skill.uuid);
    for (const skill of current.filter((s) => !wanted.includes(s))) {
      // Sequential by design: the server locks the agent for each detach, so parallel requests would only queue.
      await detachSkillFromAgent({ path: { agent_uuid: uuid, skill_uuid: skill }, throwOnError: true }); // NOSONAR
    }
    const added = wanted.filter((s) => !current.includes(s));
    if (added.length > 0) await attachSkills(added);
  };

  const steps = draft.targetUuid
    ? [
        {
          key: "save",
          label: t("steps.save"),
          run: async () => {
            await update.mutateAsync({
              path: { agent_uuid: draft.targetUuid ?? "" },
              body: body as AgentUpdate,
              throwOnError: true,
            });
          },
        },
        {
          key: "skills",
          label: t("steps.skills"),
          skip: skillUuids === undefined,
          run: () => syncSkills(skillUuids ?? []),
        },
      ]
    : [
        {
          key: "create",
          label: t("steps.create"),
          run: async () => {
            const { data } = await create.mutateAsync({
              body: {
                ...(body as AgentCreate),
                teamUuid: actions.teamUuid ?? "",
                sourceSessionUuid: actions.sessionUuid,
              },
              throwOnError: true,
            });
            agentUuid.current = data.uuid;
          },
        },
        {
          key: "skills",
          label: t("steps.skills"),
          skip: !skillUuids?.length,
          run: async () => {
            await attachSkills(skillUuids ?? []);
          },
        },
      ];
  const run = useDraftRun(draft.draftId, steps, {
    fallback: t("failed"),
    success: draft.targetUuid ? t("saved") : t("created"),
    entityUuid: () => agentUuid.current,
    invalidate: (uuid) => [
      listAgentsQueryKey(),
      getAgentQueryKey({ path: { agent_uuid: uuid } }),
      listAgentSkillsQueryKey({ path: { agent_uuid: uuid } }),
    ],
  });

  return {
    body,
    skillUuids: skillUuids ?? [],
    ...run,
    blocked: draft.targetUuid || actions.teamUuid ? null : actions.noTeam,
    openInForm: draft.targetUuid
      ? undefined
      : () =>
          navigate("/build/agents/new/form", {
            state: { draft: body, sourceSessionUuid: actions.sessionUuid } satisfies AgentFormState,
          }),
  };
}
